import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus, ChevronRight, Loader2, ClipboardList, LogOut,
  Building2, FileText, X, Sparkles, ArrowRight,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import PwaInstallPrompt from "@/components/PwaInstallPrompt";

/**
 * FieldHome — /kentta
 *
 * Kenttäkäyttäjän etusivu. TARKOITUKSELLA hyvin minimaali:
 *   • Iso "+ Aloita työ" -painike
 *   • Alla "Omat työt" -lista (avoimet yrityksen työt)
 *   • Ei minkäänlaista toimistonäkymän valikkoa, työpohjien hallintaa,
 *     asetuksia tai laskutusta. Sen ei ole tarkoituskaan olla Dashboardin
 *     versio.
 *
 * "Aloita työ" avaa `QuickStartSheet`in joka pyytää työpohjan (pakollinen),
 * työn nimen (vapaaehtoinen — täytetään työpohjasta oletuksena) ja kohteen
 * (vapaaehtoinen), luo raportin + ensimmäisen yksikön ja siirtyy SUORAAN
 * mobiiliraportointiin (/r-edit/{editToken}) — ei koskaan pysähdy ReportDetailiin.
 */
export default function FieldHome() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quickOpen, setQuickOpen] = useState(false);

  const refresh = async () => {
    try {
      const { data } = await api.get("/reports");
      setReports((data || []).filter((r) => r.status !== "completed" && r.status !== "sent"));
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, []);

  const openWork = (r) => {
    if (r?.edit_token) navigate(`/r-edit/${r.edit_token}`);
    else toast.error("Työn linkkiä ei löytynyt");
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Yläpalkki — hyvin kevyt, ei valikkoa */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center justify-between">
          <div className="font-display font-bold text-lg tracking-tight">
            <span className="text-blue-900">Veraso</span> <span className="text-emerald-600">Reports</span>
          </div>
          <button
            onClick={logout}
            className="p-2 rounded-lg text-slate-500 hover:text-slate-800 hover:bg-slate-50"
            aria-label="Kirjaudu ulos"
            data-testid="field-logout"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 pt-6 pb-24">
        <div className="mb-6">
          <div className="text-[11px] uppercase tracking-widest text-emerald-600 font-semibold mb-1">
            {user?.name ? `Hei, ${user.name.split(" ")[0]}` : "Tervetuloa"}
          </div>
          <h1 className="font-display text-2xl font-bold text-slate-900 leading-tight">
            Aloita työ tai jatka avoinna olevaa
          </h1>
        </div>

        {/* Iso Aloita työ -painike */}
        <button
          type="button"
          onClick={() => setQuickOpen(true)}
          className="w-full flex items-center gap-4 rounded-2xl bg-emerald-600 hover:bg-emerald-700 text-white p-5 shadow-lg shadow-emerald-600/20 transition-all hover:-translate-y-0.5 mb-6"
          data-testid="field-start-work-btn"
        >
          <div className="h-12 w-12 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0">
            <Plus className="h-7 w-7" />
          </div>
          <div className="flex-1 text-left">
            <div className="font-display font-bold text-lg leading-tight">Aloita työ</div>
            <div className="text-sm text-white/80 mt-0.5">Valitse työpohja ja aloita heti</div>
          </div>
          <ChevronRight className="h-5 w-5" />
        </button>

        {/* Omat työt */}
        <div>
          <div className="flex items-baseline justify-between mb-3 px-1">
            <h2 className="font-display text-lg font-semibold text-slate-900">Omat työt</h2>
            <span className="text-xs text-slate-500 tabular-nums">{reports.length}</span>
          </div>

          {loading ? (
            <div className="rounded-2xl bg-white border border-slate-200 p-8 flex items-center justify-center text-slate-500">
              <Loader2 className="h-5 w-5 animate-spin text-emerald-600" />
            </div>
          ) : reports.length === 0 ? (
            <div className="rounded-2xl bg-white border border-slate-200 p-8 text-center text-sm text-slate-500" data-testid="field-empty">
              <ClipboardList className="h-8 w-8 mx-auto mb-2 text-slate-300" />
              Ei avoinna olevia töitä. Aloita uusi työ ylhäältä.
            </div>
          ) : (
            <ul className="space-y-2" data-testid="field-work-list">
              {reports.map((r) => (
                <li key={r.report_id}>
                  <button
                    type="button"
                    onClick={() => openWork(r)}
                    className="w-full text-left flex items-center gap-3 rounded-xl bg-white border border-slate-200 hover:border-emerald-300 hover:shadow-sm transition-all px-4 py-3.5"
                    data-testid={`field-work-${r.report_id}`}
                  >
                    <div className="h-10 w-10 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center ring-1 ring-emerald-100 flex-shrink-0">
                      <FileText className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-slate-900 truncate">{r.title || r.work_type || "Työ"}</div>
                      <div className="text-xs text-slate-500 truncate flex items-center gap-1">
                        {r.customer_name && <span>{r.customer_name}</span>}
                        {r.customer_name && r.units?.length ? <span>·</span> : null}
                        {r.units?.length ? <span>{r.units.length} yksikköä</span> : null}
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-slate-300 flex-shrink-0" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </main>

      {quickOpen && (
        <QuickStartSheet onClose={() => setQuickOpen(false)} onCreated={() => { setQuickOpen(false); refresh(); }} />
      )}
      <PwaInstallPrompt />
    </div>
  );
}

/* ============================================================================
 * QuickStartSheet — Bottom sheet: valitse työpohja → luo → mobiiliraportointi
 * ========================================================================== */
function QuickStartSheet({ onClose, onCreated }) {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pickedTemplateId, setPickedTemplateId] = useState(""); // "" = tyhjä työ
  const [title, setTitle] = useState("");
  const [customer, setCustomer] = useState("");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/content-templates");
        // Backend palauttaa joko { templates: [...] } tai suoraan arrayn.
        setTemplates(Array.isArray(data) ? data : (data?.templates || []));
      } catch {
        setTemplates([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const start = async (e) => {
    e?.preventDefault();
    setCreating(true);
    try {
      const template = templates.find((t) => t.id === pickedTemplateId);
      // Kokoa payload joko työpohjan pohjalta tai tyhjänä työnä.
      // Työpohja voi sisältää myös näyttö- ja allekirjoitusasetukset — käytä
      // niitä jos määritelty, muuten kentältä aloitettu työ pyytää aina
      // allekirjoituksen ("Ohita" mahdollinen valmistelun jälkeen).
      const pick = (key, dflt) => (template && template[key] !== undefined && template[key] !== null ? template[key] : dflt);
      const payload = {
        title: (title.trim() || (template?.name || "").trim() || "Työ"),
        industry: "muu",
        property_type: pick("property_type", ""),
        work_type: (template?.name || title || "Työ").trim() || "Työ",
        unit_kind: pick("unit_kind", "huoneistot"),
        unit_count: 1,
        unit_label_template: pick("unit_label_template", "Asunto {n}"),
        customer_name: customer.trim() || null,
        site_address: null,
        executor_company: null,
        ask_executor: pick("ask_executor", false),
        image_timestamps: pick("image_timestamps", true),
        request_signature: pick("request_signature", true),
        hide_work_checklists: pick("hide_work_checklists", false),
        hide_finding_checklists: pick("hide_finding_checklists", false),
        hide_materials: pick("hide_materials", false),
        hide_kpi_cards: pick("hide_kpi_cards", true),
        base_checklist: template?.base_checklist || [],
        base_checklist_findings: template?.base_checklist_findings || [],
        materials: template?.materials || [],
        measurement_fields: template?.measurement_fields || [],
        custom_fields: template?.custom_fields || [],
      };
      const { data } = await api.post("/reports", payload);
      const token = data.edit_token;
      if (!token) throw new Error("edit_token missing");
      toast.success("Työ aloitettu");
      if (onCreated) onCreated();
      // Vie kenttätyöntekijä SUORAAN mobiiliraportointiin.
      navigate(`/r-edit/${token}`, { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Työn aloitus epäonnistui");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-6"
      onClick={onClose}
      data-testid="quick-start-sheet"
    >
      <div
        className="bg-white w-full md:max-w-md md:rounded-3xl rounded-t-3xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 md:p-6 border-b border-slate-100 flex items-start justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-emerald-600 font-semibold mb-1">Uusi työ</div>
            <h2 className="font-display text-xl md:text-2xl font-semibold text-slate-900 leading-tight">Aloita työ</h2>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-slate-100" aria-label="Sulje">
            <X className="h-5 w-5 text-slate-400" />
          </button>
        </div>

        <form onSubmit={start} className="p-5 md:p-6 space-y-5 overflow-y-auto flex-1">
          {/* Template picker */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Työpohja <span className="text-slate-400 font-normal">(valinnainen)</span></label>
            {loading ? (
              <div className="rounded-xl bg-slate-50 p-4 flex items-center justify-center">
                <Loader2 className="h-5 w-5 animate-spin text-emerald-600" />
              </div>
            ) : templates.length === 0 ? (
              <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 text-xs text-slate-500">Ei työpohjia. Aloitat tyhjän työn.</div>
            ) : (
              <div className="space-y-2" data-testid="quick-template-list">
                <button
                  type="button"
                  onClick={() => setPickedTemplateId("")}
                  className={`w-full text-left flex items-center gap-3 rounded-xl border p-3 transition-all ${
                    pickedTemplateId === "" ? "border-emerald-500 bg-emerald-50/60 ring-2 ring-emerald-500/20" : "border-slate-200 bg-white hover:border-slate-300"
                  }`}
                >
                  <div className="h-9 w-9 rounded-lg bg-slate-100 text-slate-500 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-900">Tyhjä työ</div>
                    <div className="text-xs text-slate-500">Aloita ilman valmista sisältöä</div>
                  </div>
                </button>
                {templates.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setPickedTemplateId(t.id)}
                    className={`w-full text-left flex items-center gap-3 rounded-xl border p-3 transition-all ${
                      pickedTemplateId === t.id ? "border-emerald-500 bg-emerald-50/60 ring-2 ring-emerald-500/20" : "border-slate-200 bg-white hover:border-slate-300"
                    }`}
                    data-testid={`quick-template-${t.id}`}
                  >
                    <div className="h-9 w-9 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center ring-1 ring-emerald-100 flex-shrink-0">
                      <ClipboardList className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-slate-900 truncate">{t.name}</div>
                      <div className="text-xs text-slate-500">
                        {[
                          t.base_checklist?.length ? `${t.base_checklist.length} työvaihetta` : null,
                          t.base_checklist_findings?.length ? `${t.base_checklist_findings.length} havaintoa` : null,
                          t.materials?.length ? `${t.materials.length} tarviketta` : null,
                        ].filter(Boolean).join(" · ") || "Tyhjä pohja"}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Työn nimi (valinnainen) */}
          <div>
            <label htmlFor="qs-title" className="block text-sm font-semibold text-slate-700 mb-2">Työn nimi <span className="text-slate-400 font-normal">(valinnainen)</span></label>
            <input
              id="qs-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={pickedTemplateId ? (templates.find(t => t.id === pickedTemplateId)?.name || "Työ") : "esim. IV-vuosihuolto"}
              className="input-base w-full"
              data-testid="quick-title-input"
            />
          </div>

          {/* Kohde (valinnainen) */}
          <div>
            <label htmlFor="qs-customer" className="block text-sm font-semibold text-slate-700 mb-2">Kohde tai asiakas <span className="text-slate-400 font-normal">(valinnainen)</span></label>
            <div className="relative">
              <Building2 className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                id="qs-customer"
                type="text"
                value={customer}
                onChange={(e) => setCustomer(e.target.value)}
                placeholder="esim. As Oy Esimerkki"
                className="input-base pl-10 w-full"
                data-testid="quick-customer-input"
              />
            </div>
          </div>
        </form>

        <div className="p-5 md:p-6 border-t border-slate-100 bg-slate-50/40">
          <button
            type="button"
            onClick={start}
            disabled={creating}
            className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-5 py-3.5 text-base disabled:opacity-50 shadow-sm shadow-emerald-600/20"
            data-testid="quick-start-submit"
          >
            {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
            Aloita työ
          </button>
        </div>
      </div>
    </div>
  );
}
